import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Star } from "lucide-react";
import { PACKAGES, PACKAGES_INTRO } from "@/data/content";
import { useConsultation } from "@/components/site/consultation";
import { Reveal } from "@/components/site/reveal";

export default function Packages() {
  const { open } = useConsultation();

  return (
    <section id="packages" className="bg-bone py-24 md:py-32" data-testid="packages-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
                {PACKAGES_INTRO.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif text-4xl leading-[1.05] text-navy sm:text-5xl md:text-6xl">
                {PACKAGES_INTRO.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-serif text-xl italic text-gold/90">
                {PACKAGES_INTRO.tagline}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-md text-base leading-relaxed text-navy/60">
              {PACKAGES_INTRO.description}
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px border border-navy/10 bg-navy/10 md:grid-cols-3">
          {PACKAGES.map((p, i) => {
            const featured = p.featured;
            const textMain = featured ? "text-white" : "text-navy";
            const textSoft = featured ? "text-white/60" : "text-navy/55";
            const rule = featured ? "border-white/15" : "border-navy/10";

            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative flex flex-col p-8 transition-transform duration-500 hover:-translate-y-1 lg:p-10 ${
                  featured ? "bg-navy py-14 md:-my-4" : "bg-bone"
                }`}
                data-testid={`package-card-${p.id}`}
              >
                {featured && (
                  <>
                    <span className="absolute left-0 top-0 h-1 w-full bg-gold" />
                    <span className="mb-6 inline-flex w-fit items-center gap-2 bg-gold/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold">
                      <Star className="h-3 w-3 fill-gold" /> Most Popular
                    </span>
                  </>
                )}

                <p className={`text-[11px] uppercase tracking-[0.3em] ${featured ? "text-gold" : "text-navy/40"}`}>
                  {p.tier}
                </p>
                <h3 className={`mt-3 font-serif text-4xl ${textMain}`}>{p.name}</h3>
                <p className={`mt-1 text-sm uppercase tracking-[0.15em] ${featured ? "text-white/50" : "text-navy/45"}`}>
                  {p.subtitle}
                </p>

                {/* Price block */}
                <div className={`mt-7 flex items-baseline gap-2 border-t ${rule} pt-6`}>
                  <span className={`font-serif text-5xl ${textMain}`}>{p.price}</span>
                  <span className={`text-xs uppercase tracking-[0.18em] ${textSoft}`}>
                    {p.priceUnit}
                  </span>
                </div>

                <p className={`mt-5 min-h-[3.5rem] text-sm leading-relaxed ${textSoft}`}>
                  {p.audience}
                </p>

                {p.inherits && (
                  <p className={`mt-4 text-[11px] uppercase tracking-[0.2em] ${featured ? "text-gold" : "text-gold"}`}>
                    Everything in {p.inherits}, plus:
                  </p>
                )}

                <ul className={`mt-6 flex-1 space-y-2.5 border-t ${rule} pt-6`}>
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm leading-snug ${featured ? "text-white/80" : "text-navy/75"}`}>
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {p.extras && p.extras.length > 0 && (
                  <div className={`mt-8 border-t ${rule} pt-6`}>
                    <p className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${featured ? "text-gold" : "text-gold"}`}>
                      Optional extras
                    </p>
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {p.extras.map((e) => (
                        <li
                          key={e}
                          className={`px-2.5 py-1 text-[11px] tracking-wide ${
                            featured
                              ? "border border-white/15 text-white/70"
                              : "border border-navy/15 text-navy/65"
                          }`}
                        >
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {p.note && (
                  <p className={`mt-6 text-[11px] leading-relaxed italic ${featured ? "text-white/45" : "text-navy/45"}`}>
                    {p.note}
                  </p>
                )}

                <button
                  onClick={() => open(p.name)}
                  data-testid={`package-cta-${p.id}`}
                  className={`group/btn mt-8 inline-flex items-center justify-between gap-2 px-6 py-4 text-xs font-semibold uppercase tracking-[0.15em] transition-[transform,filter,background-color,color] duration-300 hover:-translate-y-0.5 ${
                    featured
                      ? "bg-gold text-navy hover:brightness-105"
                      : "border border-navy/20 text-navy hover:border-navy hover:bg-navy hover:text-white"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </button>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
